import IntelligentContentParser from './IntelligentContentParser';
import { isAmmunitionEntry, isExplicitlyEquippedWeapon } from './EquipmentState';
import {
  inferKnownWeaponStats,
  inferWeaponMagicBonus,
  inferWeaponRange,
  inferWeaponReach
} from './combat/WeaponDamageInference';

const MASTERY_PROPERTIES = ['cleave', 'graze', 'nick', 'push', 'sap', 'slow', 'topple', 'vex'];
const WEAPON_NAME_HINTS = [
  'axe',
  'battleaxe',
  'bow',
  'club',
  'crossbow',
  'dagger',
  'dart',
  'flail',
  'glaive',
  'greataxe',
  'greatclub',
  'greatsword',
  'halberd',
  'hammer',
  'handaxe',
  'javelin',
  'lance',
  'longbow',
  'longsword',
  'mace',
  'maul',
  'morningstar',
  'pike',
  'quarterstaff',
  'rapier',
  'scimitar',
  'shortbow',
  'shortsword',
  'sickle',
  'sling',
  'spear',
  'staff',
  'sword',
  'trident',
  'war pick',
  'warhammer',
  'whip'
];
const NON_WEAPON_TYPE_HINTS = /(armor|shield|consumable|potion|tool|kit|focus|wondrous|gear|pack|valuable|currency|coin|gem|jewel)/i;
const SIMPLE_WEAPONS = new Set(['club', 'dagger', 'greatclub', 'handaxe', 'javelin', 'light crossbow', 'light hammer', 'mace', 'quarterstaff', 'shortbow', 'sickle', 'sling', 'spear', 'dart']);

const titleCase = (value) => String(value || '')
  .toLowerCase()
  .replace(/\b\w/g, (char) => char.toUpperCase())
  .trim();

const canonicalWeaponName = (value) => {
  const normalized = String(value || '').trim();
  if (/^short\s+sword$/i.test(normalized)) return 'Shortsword';
  if (/^short\s+bow$/i.test(normalized)) return 'Shortbow';
  return normalized;
};

const abilityModifier = (character, ability) => {
  const score = Number(character?.abilities?.[ability]);
  return Number.isFinite(score) ? Math.floor((score - 10) / 2) : 0;
};

const proficiencyBonus = (character) => {
  const explicit = Number(character?.proficiencyBonus);
  if (Number.isFinite(explicit)) return explicit;
  const level = Math.max(1, Number(character?.level) || 1);
  return Math.floor((level - 1) / 4) + 2;
};

const resolveWeaponProficiency = (character, weapon) => {
  const name = canonicalWeaponName(weapon?.name).toLowerCase();
  const labels = [
    ...(Array.isArray(character?.weaponProficiencies) ? character.weaponProficiencies : []),
    ...(Array.isArray(character?.proficiencies?.weapons) ? character.proficiencies.weapons : []),
    ...(Array.isArray(character?.data?.proficiencies?.weapons) ? character.data.proficiencies.weapons : [])
  ].map((value) => String(value).toLowerCase());
  if (labels.some((label) => label === name || label.includes(name))) return true;
  if (SIMPLE_WEAPONS.has(name) && labels.some((label) => label.includes('simple'))) return true;
  const className = String(character?.class || character?.classes?.[0]?.name || '').toLowerCase();
  if (className === 'rogue' && (SIMPLE_WEAPONS.has(name) || ['rapier', 'shortsword', 'hand crossbow', 'longsword'].includes(name))) return true;
  return false;
};

const derivedAttackBonus = (character, weapon) => {
  const explicit = normalizeAttackBonus(weapon);
  if (explicit != null) return explicit;
  const stats = inferKnownWeaponStats(weapon);
  if (!stats) return null;
  const strength = abilityModifier(character, 'strength');
  const dexterity = abilityModifier(character, 'dexterity');
  const finesse = Array.isArray(stats.properties) && stats.properties.some((property) => /finesse/i.test(property));
  const modifier = finesse ? Math.max(strength, dexterity) : abilityModifier(character, stats.ability || 'strength');
  return modifier + (resolveWeaponProficiency(character, weapon) ? proficiencyBonus(character) : 0) + inferWeaponMagicBonus(weapon);
};

const derivedDamageText = (character, weapon) => {
  const explicit = normalizeDamageText(weapon);
  if (explicit) return explicit;
  const stats = inferKnownWeaponStats(weapon);
  if (!stats) return '';
  const strength = abilityModifier(character, 'strength');
  const dexterity = abilityModifier(character, 'dexterity');
  const finesse = Array.isArray(stats.properties) && stats.properties.some((property) => /finesse/i.test(property));
  const modifier = (finesse ? Math.max(strength, dexterity) : abilityModifier(character, stats.ability || 'strength'))
    + inferWeaponMagicBonus(weapon);
  return `${stats.dice}${modifier ? `${modifier > 0 ? '+' : ''}${modifier}` : ''} ${stats.type}`.trim();
};

const derivedRangeText = (weapon) => {
  const reach = Number(weapon?.reach ?? weapon?.attack?.reach);
  const range = weapon?.range ?? weapon?.attack?.range;
  if (Number.isFinite(reach) && reach > 0 && range) {
    const rangedText = formatWeaponRange({
      ...weapon,
      reach: null,
      attack: weapon?.attack ? { ...weapon.attack, reach: null } : undefined
    });
    return rangedText ? `Reach ${reach} ft.; thrown ${rangedText}` : `Reach ${reach} ft.`;
  }
  return formatWeaponRange(weapon);
};

const normalizeFeetValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') {
    const match = value.match(/\d+(?:\s*\/\s*\d+)?/);
    if (match) return match[0].replace(/\s+/g, '');
  }
  return '';
};

const collectWeaponText = (entry) => [
  entry?.notes,
  Array.isArray(entry?.properties) ? entry.properties.join(', ') : entry?.properties,
  entry?.description,
  entry?.text,
  entry?.statLine,
  entry?.attack?.notes
].filter((part) => typeof part === 'string' && part.trim()).join('\n');

const normalizeAttackBonus = (entry) => {
  const candidate = entry?.attackBonus ?? entry?.toHit ?? entry?.attack?.toHit ?? entry?.attack?.attackBonus;
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
  if (typeof candidate === 'string') {
    const match = candidate.match(/[+-]?\d+/);
    if (match) {
      const parsed = Number(match[0]);
      return Number.isFinite(parsed) ? parsed : null;
    }
  }
  return null;
};

const normalizeDamageText = (entry) => {
  if (!entry) return '';
  if (typeof entry.damageFormula === 'string' && entry.damageFormula.trim()) return entry.damageFormula.trim();
  if (typeof entry.damage === 'string' && entry.damage.trim()) {
    return entry.damageType ? `${entry.damage} ${entry.damageType}`.trim() : entry.damage.trim();
  }
  if (Array.isArray(entry.damage) && entry.damage.length) {
    const first = entry.damage[0];
    if (first?.dice) return `${first.dice} ${first.type || ''}`.trim();
  }
  if (Array.isArray(entry.attack?.damage) && entry.attack.damage.length) {
    const first = entry.attack.damage[0];
    if (first?.dice) return `${first.dice} ${first.type || ''}`.trim();
  }
  return '';
};

const hasSpellShape = (entry) => Boolean(
  entry?.castingTime
  || entry?.spellRange
  || entry?.components
  || entry?.material
  || entry?.school
  || entry?.ritual != null
  || entry?.concentration != null
  || entry?.level != null
);

const getTypeLabel = (entry) => String(
  entry?.type
  || entry?.kind
  || entry?.category
  || entry?.itemType
  || entry?.entryType
  || ''
).trim().toLowerCase();

export const isShieldLikeEntry = (entry) => {
  const normalizedEntry = typeof entry === 'string'
    ? { name: entry }
    : entry;

  const name = String(normalizedEntry?.name || normalizedEntry?.title || '').trim().toLowerCase();
  const typeLabel = getTypeLabel(normalizedEntry);

  if (!name && !typeLabel) return false;
  if (typeLabel.includes('shield')) return true;
  return /\b(shield|buckler|tower shield)\b/i.test(name);
};

const isObviousNonWeaponEntry = (entry) => {
  const name = String(entry?.name || '').trim();
  const typeLabel = getTypeLabel(entry);
  const sourceSection = String(entry?.sourceSection || entry?.parserMeta?.sourceSection || '').trim().toLowerCase();
  const sourceParser = String(entry?.parserMeta?.sourceParser || '').trim().toLowerCase();
  const combinedText = collectWeaponText(entry).toLowerCase();

  if (!name) return true;
  if (isAmmunitionEntry(entry)) return true;
  if (isShieldLikeEntry(entry)) return true;
  if (/^(spellcasting|cantrips?|spells?|prepared spells?|known spells?)$/i.test(name)) return true;
  if (typeLabel && /(spell|cantrip)/.test(typeLabel)) return true;
  if (sourceParser === 'parsespellblocks' || /spell/.test(sourceSection)) return true;
  if (hasSpellShape(entry)) return true;
  if (/\b(cantrip|spellcasting|casts? a spell|spell attack)\b/.test(`${name.toLowerCase()}\n${combinedText}`)) {
    return true;
  }

  return false;
};

const looksLikeWeaponName = (entry) => {
  const normalizedName = String(entry?.name || '').trim().toLowerCase();
  if (!normalizedName) return false;

  return WEAPON_NAME_HINTS.some((hint) => normalizedName.includes(hint));
};

const looksLikeWeaponStatLine = (entry) => {
  const combined = collectWeaponText(entry);
  if (!combined) return false;

  return Boolean(
    /\b[+-]?\d+\s+to\s+hit\b/i.test(combined)
    || /\b\d+d\d+(?:\s*[+-]\s*\d+)?\b/i.test(combined)
    || /\b(range|reach)\s+\d+/i.test(combined)
    || new RegExp(`\\b(${MASTERY_PROPERTIES.join('|')})\\b`, 'i').test(combined)
  );
};

const makeWeaponFingerprint = (entry) => {
  const name = String(entry?.name || '').trim().toLowerCase();
  const statLine = String(entry?.statLine || '').trim().toLowerCase();
  const attackBonus = normalizeAttackBonus(entry);
  const damageText = normalizeDamageText(entry).toLowerCase();
  return [name, statLine, attackBonus ?? '', damageText].join('|');
};

const collectCharacterEquipmentEntries = (character) => {
  const equipment = character?.equipment;
  const objectEquipment = equipment && typeof equipment === 'object' && !Array.isArray(equipment)
    ? equipment
    : {};
  const nested = character?.data && typeof character.data === 'object' ? character.data : {};

  const describe = (entries, sourceCollection) => (Array.isArray(entries)
    ? entries.map((entry, sourceIndex) => ({ entry, sourceCollection, sourceIndex }))
    : []);

  return [
    ...describe(objectEquipment?.weapons, 'equipment.weapons'),
    ...describe(character?.equipmentDetailed?.weapons, 'equipmentDetailed.weapons'),
    ...describe(character?.weapons, 'weapons'),
    ...describe(character?.equippedWeapons, 'equippedWeapons'),
    ...describe(character?.inventory?.equippedWeapons, 'inventory.equippedWeapons'),
    ...describe(character?.inventory?.equipment?.weapons, 'inventory.equipment.weapons'),
    ...describe(nested?.equipment?.weapons, 'data.equipment.weapons'),
    ...describe(nested?.equipmentDetailed?.weapons, 'data.equipmentDetailed.weapons'),
    ...describe(nested?.equippedWeapons, 'data.equippedWeapons'),
    ...describe(nested?.inventory?.equippedWeapons, 'data.inventory.equippedWeapons'),
    ...describe(nested?.inventory?.equipment?.weapons, 'data.inventory.equipment.weapons')
  ].filter(({ entry, sourceCollection }) => isExplicitlyEquippedWeapon(entry, sourceCollection));
};

export const inferWeaponLikeEntry = (entry) => {
  if (!entry || (typeof entry !== 'object' && typeof entry !== 'string')) return null;

  const normalizedEntry = typeof entry === 'string'
    ? { name: entry }
    : { ...entry };
  const typeLabel = getTypeLabel(normalizedEntry);
  const nameHint = looksLikeWeaponName(normalizedEntry);
  const statLineHint = looksLikeWeaponStatLine(normalizedEntry);
  const attackHint = normalizeAttackBonus(normalizedEntry) !== null;
  const damageHint = Boolean(normalizeDamageText(normalizedEntry));
  const rangeHint = Boolean(formatWeaponRange(normalizedEntry));
  const masteryHint = Boolean(extractWeaponMastery(normalizedEntry));
  const explicitWeaponType = /\bweapon\b/i.test(typeLabel);
  const explicitNonWeaponType = NON_WEAPON_TYPE_HINTS.test(typeLabel);
  const weaponLikeProfile = attackHint || damageHint || rangeHint || masteryHint || statLineHint;

  if (isObviousNonWeaponEntry(normalizedEntry)) return null;
  if (explicitNonWeaponType && !weaponLikeProfile && !nameHint) return null;
  if (!explicitWeaponType && !weaponLikeProfile && !nameHint) return null;

  const normalizedWeapon = IntelligentContentParser.normalizeEquipmentWeaponEntry({
    ...normalizedEntry,
    type: 'weapon'
  });

  if (isObviousNonWeaponEntry(normalizedWeapon)) return null;
  return normalizedWeapon;
};

export const formatWeaponRange = (entry) => {
  const range = entry?.range ?? entry?.attack?.range ?? null;
  const reach = entry?.reach ?? entry?.attack?.reach ?? null;
  const combinedText = collectWeaponText(entry);

  if (range && typeof range === 'object') {
    const shortRange = normalizeFeetValue(
      range.normal ?? range.value ?? range.short ?? range.range
    );
    const longRange = normalizeFeetValue(range.long ?? range.maximum ?? range.max);
    if (shortRange && longRange) return `${shortRange}/${longRange} ft.`;
    if (shortRange) return `${shortRange} ft.`;
  }

  if (typeof range === 'number' && Number.isFinite(range)) {
    return `${range} ft.`;
  }

  if (typeof range === 'string' && range.trim()) {
    const normalized = range.trim();
    const slashMatch = normalized.match(/(\d+)\s*\/\s*(\d+)/);
    if (slashMatch) return `${slashMatch[1]}/${slashMatch[2]} ft.`;
    const numberMatch = normalized.match(/\d+/);
    if (numberMatch) return `${numberMatch[0]} ft.`;
    return normalized;
  }

  if (typeof reach === 'number' && Number.isFinite(reach)) {
    return `Reach ${reach} ft.`;
  }

  if (typeof reach === 'string' && reach.trim()) {
    const numberMatch = reach.match(/\d+/);
    return numberMatch ? `Reach ${numberMatch[0]} ft.` : reach.trim();
  }

  if (combinedText) {
    const rangeMatch = combinedText.match(/\brange\s+(\d+)(?:\s*\/\s*(\d+))?\b/i);
    if (rangeMatch) {
      return rangeMatch[2]
        ? `${rangeMatch[1]}/${rangeMatch[2]} ft.`
        : `${rangeMatch[1]} ft.`;
    }

    const reachMatch = combinedText.match(/\breach\s+(\d+)\b/i);
    if (reachMatch) {
      return `Reach ${reachMatch[1]} ft.`;
    }
  }

  return '';
};

export const extractWeaponMastery = (entry) => {
  const directCandidates = [
    entry?.masteryProperty,
    entry?.weaponMastery,
    entry?.mastery
  ].filter(Boolean);

  if (directCandidates.length > 0) {
    return directCandidates
      .flatMap((value) => Array.isArray(value) ? value : [value])
      .map(titleCase)
      .filter(Boolean)
      .join(', ');
  }

  const combined = collectWeaponText(entry);
  if (!combined) return '';

  const found = new Set();
  const masteryPattern = new RegExp(`\\b(${MASTERY_PROPERTIES.join('|')})\\b(?:\\s+mastery)?`, 'gi');
  let match;
  while ((match = masteryPattern.exec(combined)) !== null) {
    found.add(titleCase(match[1]));
  }

  return Array.from(found).join(', ');
};

export const buildWeaponNotes = (entry, fallback = '') => {
  const rawText = collectWeaponText({
    ...entry,
    notes: entry?.notes ?? fallback
  });
  if (!rawText) return '';

  const mastery = extractWeaponMastery(entry);
  const fragments = rawText
    .split(/[\n,;]+/)
    .map((fragment) => fragment.trim())
    .filter(Boolean)
    .map((fragment) => fragment
      .replace(new RegExp(`^(${MASTERY_PROPERTIES.join('|')})\\s+mastery\\s*[-:]?\\s*`, 'i'), '')
      .replace(/^(range|reach)\s+[-:\s]*/i, '')
      .trim()
    )
    .filter(Boolean)
    .filter((fragment) => !/^[+-]?\d+\s*to\s*hit$/i.test(fragment))
    .filter((fragment) => !/^\d+d\d+(?:\s*[+-]\s*\d+)?(?:\s+(?:piercing|slashing|bludgeoning|fire|cold|lightning|thunder|acid|poison|necrotic|radiant|force|psychic|magic))?$/i.test(fragment))
    .filter((fragment) => !/^(piercing|slashing|bludgeoning|fire|cold|lightning|thunder|acid|poison|necrotic|radiant|force|psychic|magic)$/i.test(fragment))
    .filter((fragment) => !/^[bps]$/i.test(fragment))
    .filter((fragment) => !/^\d+\s*\/\s*\d+\s*(ft\.?)?$/i.test(fragment))
    .filter((fragment) => !/^\d+\s*(ft\.?)?$/i.test(fragment))
    .filter((fragment) => !/^(range|reach)\b/i.test(fragment))
    .filter((fragment) => !new RegExp(`^(${MASTERY_PROPERTIES.join('|')})$`, 'i').test(fragment))
    .filter((fragment) => !(mastery && fragment.toLowerCase() === mastery.toLowerCase()));

  return Array.from(new Set(fragments)).join('; ');
};

export const buildCharacterWeaponTableEntries = (character) => {
  const seen = new Set();
  const rows = [];

  collectCharacterEquipmentEntries(character).forEach(({ entry, sourceCollection, sourceIndex }) => {
    const normalizedWeapon = inferWeaponLikeEntry(entry);
    if (!normalizedWeapon) return;
    normalizedWeapon.name = canonicalWeaponName(normalizedWeapon.name);
    normalizedWeapon.range = normalizedWeapon.range || inferWeaponRange(normalizedWeapon);
    normalizedWeapon.reach = normalizedWeapon.reach || inferWeaponReach(normalizedWeapon);

    const fingerprint = makeWeaponFingerprint(normalizedWeapon);
    if (seen.has(fingerprint)) return;
    seen.add(fingerprint);

    rows.push({
      key: `${sourceCollection}-${sourceIndex}`,
      kind: 'weapon',
      name: normalizedWeapon?.name || 'Weapon',
      attackBonus: derivedAttackBonus(character, normalizedWeapon),
      reach: normalizedWeapon?.reach ?? normalizedWeapon?.attack?.reach ?? null,
      range: normalizedWeapon?.range ?? normalizedWeapon?.attack?.range ?? null,
      damageText: derivedDamageText(character, normalizedWeapon),
      rangeText: derivedRangeText(normalizedWeapon),
      masteryText: extractWeaponMastery(normalizedWeapon),
      proficiencyText: resolveWeaponProficiency(character, normalizedWeapon) ? 'Proficient' : 'Not proficient',
      notes: buildWeaponNotes(normalizedWeapon, normalizedWeapon?.statLine || ''),
      sourceCollection,
      sourceIndex
    });
  });

  rows.sort((a, b) => {
    const aMelee = a?.reach != null;
    const bMelee = b?.reach != null;
    if (aMelee !== bMelee) return aMelee ? -1 : 1;

    const aRanged = a?.range != null;
    const bRanged = b?.range != null;
    if (aRanged !== bRanged) return aRanged ? -1 : 1;

    return String(a.name || '').localeCompare(String(b.name || ''));
  });

  return rows;
};
